import type { SerializedNode, ConversionContext, RGBA, Gradient } from '../types';
import type { CustomText } from '../../types/text';
import type { Color } from '../../types/layout';
import type { Descendant } from 'slate';
import { getAttribute, getChildElements } from '../parser';
import {
  parsePadding,
  parseBorderRadius,
  parseInteger,
  parseFloat_,
  parseBoolean,
  parsePosition,
  parsePositionOffset,
  inferPositionAnchorsState,
} from '../utils';
import { parseColor } from '../utils/color-parser';
import { TEXT_DEFAULTS, getLeafFillSpace } from '../utils/defaults';
import { TEXT_NODE_NAME, EXTERNAL_LINK_EVENT } from '../../utils/constants';
import { checkIsGradient } from '../../utils/styleHelpers';

/**
 * Valid attributes for text elements
 * When attached="false", container styling attributes become configurable
 */
export const TEXT_ATTRIBUTES = [
  // Core attributes
  'variant',
  'align',
  'color',
  'padding',
  'margin',
  'borderRadius',
  'maxWidth',
  // Global style toggle
  'attached',
  // Container styling (when detached)
  'backgroundColor',
  'lineHeight',
  'width',
  // Position attributes
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
  // Tablet breakpoint overrides
  'tablet-width',
  'tablet-padding',
  'tablet-margin',
  'tablet-borderRadius',
  'tablet-maxWidth',
  'tablet-position',
  'tablet-top',
  'tablet-left',
  'tablet-right',
  'tablet-bottom',
  'tablet-zIndex',
  // Mobile breakpoint overrides
  'mobile-width',
  'mobile-padding',
  'mobile-margin',
  'mobile-borderRadius',
  'mobile-maxWidth',
  'mobile-position',
  'mobile-top',
  'mobile-left',
  'mobile-right',
  'mobile-bottom',
  'mobile-zIndex',
] as const;

/**
 * Valid attributes for paragraph elements within text
 * These attributes apply to all text leaves within the paragraph
 */
export const PARAGRAPH_ATTRIBUTES = [
  // Always applied (attached or detached)
  'color',
  'textDecoration',
  // Only when detached (attached="false")
  'fontSize',
  'fontWeight',
  'fontFamily',
  'letterSpacing',
  'textTransform',
  'fontStyle',
] as const;

/**
 * Valid attributes for link elements within text
 */
export const LINK_ATTRIBUTES = [
  // Core link attributes
  'href',
  'target',
  'variant',
  // Styling overrides (same rules as paragraph attributes)
  'color',
  'textDecoration',
  'fontSize',
  'fontWeight',
  'fontFamily',
  'letterSpacing',
  'textTransform',
  'fontStyle',
] as const;

/**
 * Check if an attribute is valid for text elements
 */
export const isValidTextAttribute = (attr: string): boolean => {
  return (TEXT_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Check if an attribute is valid for link elements
 */
export const isValidLinkAttribute = (attr: string): boolean => {
  return (LINK_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Check if an attribute is valid for paragraph elements
 */
export const isValidParagraphAttribute = (attr: string): boolean => {
  return (PARAGRAPH_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Build a click event object for a link
 */
const buildLinkClickEvent = (linkElement: Element) => {
  const href = getAttribute(linkElement, 'href') || '';
  const target = getAttribute(linkElement, 'target');
  const openInNewTab = target === '_blank';

  return {
    eventType: EXTERNAL_LINK_EVENT,
    eventData: {
      url: href,
      openInNewTab,
    },
  };
};

/**
 * Parse text styling attributes from an element (paragraph or link)
 * Respects attached/detached rules:
 * - color, textDecoration: always applied
 * - fontSize, fontWeight, fontFamily, letterSpacing, textTransform, fontStyle: only when detached
 *
 * @param element - The XML element to parse attributes from
 * @param appliedGlobalStyle - Whether the text is attached to global styles
 * @returns Partial CustomText with parsed style properties
 */
const parseTextStyleAttributes = (
  element: Element,
  appliedGlobalStyle: boolean,
): Partial<CustomText> => {
  const styles: Partial<CustomText> = {};

  // Always applied (attached or detached)
  const colorAttr = getAttribute(element, 'color');
  if (colorAttr) {
    const parsedColor = parseColor(colorAttr);
    // Cast to Color - ColorRef is supported at runtime via getGlobalStyleColor
    if (parsedColor) styles.color = parsedColor as Color;
  }

  const textDecorationAttr = getAttribute(element, 'textDecoration');
  if (textDecorationAttr) styles.textDecoration = textDecorationAttr;

  // Only when detached (attached="false")
  if (!appliedGlobalStyle) {
    const fontSizeAttr = getAttribute(element, 'fontSize');
    if (fontSizeAttr) styles.fontSize = fontSizeAttr;

    const fontWeightAttr = getAttribute(element, 'fontWeight');
    if (fontWeightAttr) styles.fontWeight = fontWeightAttr;

    const fontFamilyAttr = getAttribute(element, 'fontFamily');
    if (fontFamilyAttr) styles.fontFamily = fontFamilyAttr;

    const letterSpacingAttr = getAttribute(element, 'letterSpacing');
    if (letterSpacingAttr) {
      const parsed = parseFloat_(letterSpacingAttr, undefined);
      if (parsed !== undefined) styles.letterSpacing = parsed;
    }

    const textTransformAttr = getAttribute(element, 'textTransform');
    if (textTransformAttr) styles.textTransform = textTransformAttr;

    const fontStyleAttr = getAttribute(element, 'fontStyle');
    if (fontStyleAttr) styles.fontStyle = fontStyleAttr;

    const lineHeightAttr = getAttribute(element, 'lineHeight');
    if (lineHeightAttr) {
      const parsed = parseFloat_(lineHeightAttr, undefined);
      if (parsed !== undefined) (styles as Record<string, unknown>).lineHeight = parsed;
    }
  }

  return styles;
};

/**
 * Read inline style attributes from a <paragraph> element and apply them
 * to all CustomText children of that paragraph.
 * Only applies styles in detached mode (appliedGlobalStyle = false).
 */
const applyParagraphInlineStyles = (
  paragraphElement: Element,
  children: CustomText[],
  appliedGlobalStyle: boolean,
): void => {
  // Paragraph inline styles (fontSize, fontFamily, fontWeight, lineHeight, letterSpacing)
  // should only be applied in detached mode
  if (appliedGlobalStyle) return;

  const fontSize = getAttribute(paragraphElement, 'fontSize');
  const fontFamily = getAttribute(paragraphElement, 'fontFamily');
  const fontWeight = getAttribute(paragraphElement, 'fontWeight');
  const lineHeightAttr = getAttribute(paragraphElement, 'lineHeight');
  const letterSpacingAttr = getAttribute(paragraphElement, 'letterSpacing');

  const hasInlineStyles =
    fontSize || fontFamily || fontWeight || lineHeightAttr || letterSpacingAttr;
  if (!hasInlineStyles) return;

  for (const child of children) {
    if (fontSize) child.fontSize = fontSize;
    if (fontFamily) child.fontFamily = fontFamily;
    if (fontWeight) child.fontWeight = fontWeight;
    if (lineHeightAttr) {
      (child as Record<string, unknown>).lineHeight = parseFloat_(lineHeightAttr);
    }
    if (letterSpacingAttr) {
      child.letterSpacing = parseFloat_(letterSpacingAttr);
    }
  }
};

/**
 * Parse text and link nodes within a <paragraph> element
 *
 * @param paragraphElement - The <paragraph> XML element
 * @param appliedGlobalStyle - Whether the text is attached to global styles
 * @returns Array of CustomText objects for Slate
 */
const parseTextNodes = (paragraphElement: Element, appliedGlobalStyle: boolean): CustomText[] => {
  const children: CustomText[] = [];

  // Parse paragraph-level styles
  const paragraphStyles = parseTextStyleAttributes(paragraphElement, appliedGlobalStyle);

  for (const node of paragraphElement.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      // Include whitespace-only text for proper spacing between elements
      if (text) {
        // Apply paragraph styles to plain text nodes
        children.push({ text, ...paragraphStyles });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      if (el.tagName.toLowerCase() === 'link') {
        const variant = getAttribute(el, 'variant');
        // Parse link-specific style overrides
        const linkStyles = parseTextStyleAttributes(el, appliedGlobalStyle);

        const textNode: CustomText = {
          text: el.textContent || '',
          clickEvent: buildLinkClickEvent(el),
          // Apply paragraph styles first, then link overrides
          ...paragraphStyles,
          ...linkStyles,
        };
        if (variant) {
          textNode.selectedLinkVariant = variant as CustomText['selectedLinkVariant'];
        }
        children.push(textNode);
      }
      // Other element types inside <paragraph> are ignored
    }
  }

  applyParagraphInlineStyles(paragraphElement, children, appliedGlobalStyle);

  return children;
};

/**
 * Parse <paragraph> elements within a <text> element into Slate Descendant array
 *
 * @param element - The <text> XML element
 * @param context - Conversion context for error reporting
 * @param appliedGlobalStyle - Whether the text is attached to global styles
 * @returns Array of Slate Descendant objects (paragraphs)
 */
const parseParagraphElements = (
  element: Element,
  context: ConversionContext,
  appliedGlobalStyle: boolean,
): Descendant[] => {
  const paragraphs: Descendant[] = [];
  const paragraphElements = getChildElements(element, 'paragraph');

  // Require <paragraph> tags - warn if none found
  if (paragraphElements.length === 0) {
    context.errors.push({
      severity: 'warning',
      type: 'structural',
      message: 'Text element should contain <paragraph> tags for content',
      element: 'text',
    });
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }

  for (const paragraphElement of paragraphElements) {
    const children = parseTextNodes(paragraphElement, appliedGlobalStyle);
    paragraphs.push({
      type: 'paragraph',
      children: children.length > 0 ? children : [{ text: '' }],
    });
  }

  return paragraphs;
};

/**
 * Transform a <text> XML element into a CraftJS Text node
 *
 * By default, text elements use global styles (attached="true"). When attached="false",
 * container styling properties become configurable via XML attributes.
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the Text
 */
export const transformText = (element: Element, context: ConversionContext): SerializedNode => {
  // Parse attached attribute - maps to appliedGlobalStyle
  const attachedAttr = getAttribute(element, 'attached');
  const appliedGlobalStyle = parseBoolean(attachedAttr, true);

  // Parse layout attributes
  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, TEXT_DEFAULTS.padding);

  const marginAttr = getAttribute(element, 'margin');
  const margin = parsePadding(marginAttr, TEXT_DEFAULTS.margin);

  const borderRadiusAttr = getAttribute(element, 'borderRadius');
  const borderRadius = parseBorderRadius(borderRadiusAttr, TEXT_DEFAULTS.borderRadius);

  const maxWidthAttr = getAttribute(element, 'maxWidth');
  const maxWidth = parseInteger(maxWidthAttr, TEXT_DEFAULTS.maxWidth);

  // Parse width
  const widthAttr = getAttribute(element, 'width');
  const width = widthAttr || TEXT_DEFAULTS.width;

  // Parse global style variant
  const variantAttr = getAttribute(element, 'variant');
  const selectedStyleVariant = variantAttr || TEXT_DEFAULTS.selectedStyleVariant;

  // Parse text alignment
  const alignAttr = getAttribute(element, 'align');
  const textAlign = alignAttr || TEXT_DEFAULTS.textAlign;

  // Parse color override
  const colorAttr = getAttribute(element, 'color');
  const parsedColor = parseColor(colorAttr);

  // Parse container styling (used when attached="false")
  const backgroundColorAttr = getAttribute(element, 'backgroundColor');
  const backgroundColor = backgroundColorAttr
    ? parseColor(backgroundColorAttr)
    : TEXT_DEFAULTS.backgroundColor;

  const lineHeightAttr = getAttribute(element, 'lineHeight');
  const lineHeight = parseFloat_(lineHeightAttr, TEXT_DEFAULTS.lineHeight);

  // Build textStyles from <paragraph> elements
  const textStyles = parseParagraphElements(element, context, appliedGlobalStyle);

  // Apply text-level color as fallback to leaf nodes without their own color
  // When applied, also set textSolid for RGBA or textGradient for gradients
  if (parsedColor) {
    for (const paragraph of textStyles) {
      const p = paragraph as Record<string, unknown>;
      const children = p.children as Record<string, unknown>[];
      for (const child of children) {
        // Only apply if the leaf doesn't already have a color from paragraph/link attributes
        if (!child.color) {
          child.color = parsedColor;
          if (typeof parsedColor !== 'string') {
            if (checkIsGradient(parsedColor)) {
              child.textGradient = parsedColor as Gradient;
            } else {
              child.textSolid = parsedColor as RGBA;
            }
          }
        }
      }
    }
  }

  // Parse position attributes
  const positionAttr = getAttribute(element, 'position');
  const position = parsePosition(positionAttr) || TEXT_DEFAULTS.position;

  const topAttr = getAttribute(element, 'top');
  const top = parsePositionOffset(topAttr);

  const leftAttr = getAttribute(element, 'left');
  const left = parsePositionOffset(leftAttr);

  const rightAttr = getAttribute(element, 'right');
  const right = parsePositionOffset(rightAttr);

  const bottomAttr = getAttribute(element, 'bottom');
  const bottom = parsePositionOffset(bottomAttr);

  const zIndexAttr = getAttribute(element, 'zIndex');
  const zIndex = parseInteger(zIndexAttr, TEXT_DEFAULTS.zIndex);

  // Infer position anchors state from provided values
  const positionAnchorsState = inferPositionAnchorsState(position, top, left, right, bottom);

  // Parse tablet breakpoint overrides
  const tablet = parseTextBreakpointOverrides(element, 'tablet');

  // Parse mobile breakpoint overrides
  const mobile = parseTextBreakpointOverrides(element, 'mobile');

  // Build props object
  const props: Record<string, unknown> = {
    textStyles,
    appliedGlobalStyle,
    selectedStyleVariant,
    textAlign,
    padding,
    margin,
    borderRadius,
    maxWidth,
    maxWidthUnit: TEXT_DEFAULTS.maxWidthUnit,
    width,
    height: TEXT_DEFAULTS.height,
    fillSpace: getLeafFillSpace(context.parentFlexDirection, TEXT_DEFAULTS.fillSpace),
    lineHeight,
    backgroundColor,
    shadow: TEXT_DEFAULTS.shadow,
    position,
    zIndex,
    top: top || TEXT_DEFAULTS.top,
    left: left || TEXT_DEFAULTS.left,
    right: right || TEXT_DEFAULTS.right,
    bottom: bottom || TEXT_DEFAULTS.bottom,
    positionAnchorsState,
    visibility: TEXT_DEFAULTS.visibility,
  };

  // Add breakpoint overrides if present
  if (Object.keys(tablet).length > 0) props.tablet = tablet;
  if (Object.keys(mobile).length > 0) props.mobile = mobile;

  return {
    type: { resolvedName: TEXT_NODE_NAME },
    isCanvas: false,
    props,
    displayName: TEXT_NODE_NAME,
    custom: {
      displayName: TEXT_NODE_NAME,
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Parse breakpoint-specific overrides (tablet-* or mobile-* prefixed attributes)
 */
const parseTextBreakpointOverrides = (
  element: Element,
  breakpoint: 'tablet' | 'mobile',
): Record<string, unknown> => {
  const overrides: Record<string, unknown> = {};

  const widthAttr = getAttribute(element, `${breakpoint}-width`);
  if (widthAttr) overrides.width = widthAttr;

  const paddingAttr = getAttribute(element, `${breakpoint}-padding`);
  if (paddingAttr) overrides.padding = parsePadding(paddingAttr, TEXT_DEFAULTS.padding);

  const marginAttr = getAttribute(element, `${breakpoint}-margin`);
  if (marginAttr) overrides.margin = parsePadding(marginAttr, TEXT_DEFAULTS.margin);

  const borderRadiusAttr = getAttribute(element, `${breakpoint}-borderRadius`);
  if (borderRadiusAttr)
    overrides.borderRadius = parseBorderRadius(borderRadiusAttr, TEXT_DEFAULTS.borderRadius);

  const maxWidthAttr = getAttribute(element, `${breakpoint}-maxWidth`);
  if (maxWidthAttr) overrides.maxWidth = parseInteger(maxWidthAttr, undefined);

  // Position attributes
  const positionAttr = getAttribute(element, `${breakpoint}-position`);
  if (positionAttr) {
    const parsedPosition = parsePosition(positionAttr);
    if (parsedPosition) overrides.position = parsedPosition;
  }

  const topAttr = getAttribute(element, `${breakpoint}-top`);
  const leftAttr = getAttribute(element, `${breakpoint}-left`);
  const rightAttr = getAttribute(element, `${breakpoint}-right`);
  const bottomAttr = getAttribute(element, `${breakpoint}-bottom`);

  const top = parsePositionOffset(topAttr);
  const left = parsePositionOffset(leftAttr);
  const right = parsePositionOffset(rightAttr);
  const bottom = parsePositionOffset(bottomAttr);

  if (top !== undefined) overrides.top = top;
  if (left !== undefined) overrides.left = left;
  if (right !== undefined) overrides.right = right;
  if (bottom !== undefined) overrides.bottom = bottom;

  // Infer positionAnchorsState if any position props are provided
  const hasPositionOverride = positionAttr || topAttr || leftAttr || rightAttr || bottomAttr;
  if (hasPositionOverride) {
    const breakpointPosition = parsePosition(positionAttr);
    overrides.positionAnchorsState = inferPositionAnchorsState(
      breakpointPosition,
      top,
      left,
      right,
      bottom,
    );
  }

  const zIndexAttr = getAttribute(element, `${breakpoint}-zIndex`);
  if (zIndexAttr) overrides.zIndex = parseInteger(zIndexAttr, 0);

  return overrides;
};
